import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '../../../design-system'
import {
  signInWithEmail,
  signInWithGoogle,
  signUpWithEmail,
  checkEmailAccount,
  validateBetaCode
} from '../services/auth.service'
import './Login.css'

/* ============================================================
   Auth Popin — modale unifiée « email-first » en surimpression
   de la landing (route /login ; /signup redirige ici).
   Flow :
     0. beta-access → code d'accès beta, validé SERVEUR via la
                      callable validateBetaCode (gate UX — le vrai
                      verrou est le claim `beta` posé par
                      redeemBetaCode et exigé par les règles
                      Firestore à la création du doc users/{uid})
     1. email    → OAuth (Google) + input email
     2. password → compte existant avec mot de passe
     3. create-password → nouveau compte : choix du mot de passe,
                   création côté client + email de vérification
                   NATIF Firebase (lien, écran /email-verification)
   ============================================================ */

type Step = 'beta-access' | 'email' | 'password' | 'create-password'

const PASSWORD_MIN_LENGTH = 8

const PROVIDER_LABELS: Record<string, string> = {
  'google.com': 'Google'
}

/* ---------- Icons ---------- */
function EyeIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    )
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  )
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
const Login: React.FC = () => {
  const navigate = useNavigate()
  // L'étape du code d'accès précède toujours le login : il est validé
  // serveur (validateBetaCode) et redemandé à chaque session — pas de
  // flag sessionStorage forgeable.
  const [step, setStep] = useState<Step>('beta-access')
  const [betaCode, setBetaCode] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')

  // Fermeture de la popin : retour à la landing
  const handleClose = () => navigate('/')

  // Échap ferme la popin + scroll de la landing verrouillé pendant l'ouverture
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }
    document.addEventListener('keydown', onKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const resetMessages = () => {
    if (error) setError('')
    if (info) setInfo('')
  }

  // Retour à l'étape email (lien « Use a different email »)
  const resetToEmailStep = () => {
    setStep('email')
    setPassword('')
    setConfirmPassword('')
    setError('')
    setInfo('')
  }

  /* ---------- Étape 0 : code d'accès beta ---------- */
  const handleBetaAccessSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    const result = await validateBetaCode(betaCode)

    if (result.success && result.valid) {
      setStep('email')
    } else {
      setError(result.error || 'Wrong access code')
    }
    setIsLoading(false)
  }

  /* ---------- Étape 1 : email → routing ---------- */
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setInfo('')
    setIsLoading(true)

    const check = await checkEmailAccount(email)

    if (!check.success) {
      setError(check.error || 'An error occurred')
      setIsLoading(false)
      return
    }

    const providers = check.providers || []

    if (check.exists && providers.includes('password')) {
      // Compte classique → champ mot de passe
      setStep('password')
      setIsLoading(false)
      return
    }

    if (check.exists) {
      // Compte OAuth-only → on pointe le bon bouton
      const label = PROVIDER_LABELS[providers[0]] || 'a social provider'
      setInfo(`This email is registered with ${label} — use the button above to continue.`)
      setIsLoading(false)
      return
    }

    // Nouveau compte → création du mot de passe (la vérification email
    // est le lien natif Firebase, envoyé à la création).
    setStep('create-password')
    setIsLoading(false)
  }

  /* ---------- Étape 2 : mot de passe (compte existant) ---------- */
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    const result = await signInWithEmail(email, password, betaCode)

    if (result.success) {
      navigate('/projects')
    } else {
      setError(result.error || 'An error occurred')
    }
    setIsLoading(false)
  }

  /* ---------- Étape 3 : créer un compte avec mot de passe ---------- */
  const handleCreatePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password.length < PASSWORD_MIN_LENGTH) {
      setError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters long`)
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsLoading(true)
    // Compte créé côté client + claim `beta` posé serveur via le code
    // d'accès (redeemBetaCode, requis pour créer le doc users/{uid}) +
    // email de vérification natif Firebase → l'écran /email-verification
    // prend le relais (flow historique).
    const result = await signUpWithEmail({ email, password, betaCode })

    if (result.success) {
      localStorage.setItem('verificationEmail', email)
      navigate('/email-verification')
    } else {
      setError(result.error || 'An error occurred')
    }
    setIsLoading(false)
  }

  /* ---------- OAuth ---------- */
  const handleOAuth = async () => {
    setIsLoading(true)
    setError('')
    setInfo('')

    const result = await signInWithGoogle(betaCode)

    if (!result.success) {
      setError(result.error || 'An error occurred')
      setIsLoading(false)
    }
    // Si le sign-in réussit, PublicRoute redirige vers /projects.
  }

  /* ---------- Titre / sous-titre par étape ---------- */
  const titles: Record<Step, { title: string; subtitle: string }> = {
    'beta-access': { title: 'Join the beta', subtitle: 'Enter your access code to continue' },
    email: { title: 'Log in or sign up', subtitle: 'Enter your email to continue' },
    password: { title: 'Welcome back', subtitle: `Enter the password for ${email}` },
    'create-password': { title: 'Create your account', subtitle: `Choose a password for ${email} — 8+ characters` }
  }
  const { title, subtitle } = titles[step]

  const passwordField = (
    name: string,
    label: string,
    placeholder: string,
    value: string,
    onChange: (v: string) => void,
    autoComplete: string,
    withToggle: boolean
  ) => (
    <div className="Login__field">
      <label className="Login__label" htmlFor={name}>{label}</label>
      <div className="Login__input-wrap">
        <input
          type={showPassword ? 'text' : 'password'}
          id={name}
          name={name}
          className="Login__input"
          placeholder={placeholder}
          value={value}
          onChange={(e) => { onChange(e.target.value); resetMessages() }}
          required
          autoComplete={autoComplete}
        />
        {withToggle && (
          <button
            type="button"
            className="Login__toggle"
            onClick={() => setShowPassword(!showPassword)}
          >
            <EyeIcon open={showPassword} />
          </button>
        )}
      </div>
    </div>
  )

  return (
    // Clic sur le backdrop = fermeture (le panel stoppe la propagation)
    <div className="Login" onClick={handleClose}>
      <div className="Login__panel" onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button type="button" className="Login__close" onClick={handleClose} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <h1 className="Login__title">{title}</h1>
        <p className="Login__subtitle">{subtitle}</p>

        {error && <div className="Login__error">{error}</div>}
        {info && <div className="Login__info">{info}</div>}

        {/* ---------- Étape 0 : code d'accès beta ---------- */}
        {step === 'beta-access' && (
          <form className="Login__form" onSubmit={handleBetaAccessSubmit}>
            {passwordField('betaCode', 'Access code', 'Your beta access code', betaCode, setBetaCode, 'off', true)}

            <Button type="submit" variant="primary" size="md" fullWidth disabled={isLoading} loading={isLoading}>
              Continue
            </Button>
          </form>
        )}

        {/* ---------- Étape 1 : email + OAuth ---------- */}
        {step === 'email' && (
          <>
            <button className="Login__oauth" onClick={() => handleOAuth()} disabled={isLoading}>
              <GoogleIcon />
              Continue with Google
            </button>

            <div className="Login__divider">
              <span>or</span>
            </div>

            <form className="Login__form" onSubmit={handleEmailSubmit}>
              <div className="Login__field">
                <label className="Login__label" htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="Login__input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); resetMessages() }}
                  required
                  autoComplete="email"
                />
              </div>

              <Button type="submit" variant="primary" size="md" fullWidth disabled={isLoading} loading={isLoading}>
                Continue
              </Button>
            </form>
          </>
        )}

        {/* ---------- Étape 2 : mot de passe (compte existant) ---------- */}
        {step === 'password' && (
          <form className="Login__form" onSubmit={handlePasswordSubmit}>
            {passwordField('password', 'Password', 'Your password', password, setPassword, 'current-password', true)}

            <Button type="submit" variant="primary" size="md" fullWidth disabled={isLoading} loading={isLoading}>
              Sign in
            </Button>
          </form>
        )}

        {/* ---------- Étape 3 : créer un compte avec mot de passe ---------- */}
        {step === 'create-password' && (
          <form className="Login__form" onSubmit={handleCreatePasswordSubmit}>
            {passwordField('password', 'Password', '8+ characters', password, setPassword, 'new-password', true)}
            {passwordField('confirmPassword', 'Confirm password', 'Repeat password', confirmPassword, setConfirmPassword, 'new-password', false)}

            <Button type="submit" variant="primary" size="md" fullWidth disabled={isLoading} loading={isLoading}>
              Create account
            </Button>
          </form>
        )}

        {/* Retour à l'étape email */}
        {(step === 'password' || step === 'create-password') && (
          <p className="Login__signup">
            <button type="button" className="Login__link-button" onClick={resetToEmailStep} disabled={isLoading}>
              ← Use a different email
            </button>
          </p>
        )}

        {/* Mentions légales (liens en petit, pas de checkbox —
            « continuer » vaut acceptation) */}
        <p className="Login__legal">
          <Link to="/terms" className="Login__legal-link">Terms of Use</Link>
          {' · '}
          <Link to="/privacy" className="Login__legal-link">Privacy Policy</Link>
        </p>
      </div>
    </div>
  )
}

export default Login
